import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../services/api';

export default function VolumeMonitor() {
  const navigate = useNavigate();
  const [stats, setStats] = useState(null);
  const [alerts, setAlerts] = useState([]);
  const [settings, setSettings] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showSettings, setShowSettings] = useState(false);
  const [saving, setSaving] = useState(false);
  const [filter, setFilter] = useState({ status: '', minScore: '' });
  const [chartModal, setChartModal] = useState({ isOpen: false, alert: null });

  const fetchData = useCallback(async () => {
    try {
      const [statsRes, alertsRes] = await Promise.all([
        api.get('/volume-scanner/stats'),
        api.get('/volume-scanner/alerts', { params: filter })
      ]);
      setStats(statsRes.data);
      setAlerts(alertsRes.data.alerts || []);
      if (statsRes.data.settings && !settings) {
        setSettings(statsRes.data.settings);
      }
    } catch (err) {
      console.error('Failed to fetch volume data:', err);
    } finally {
      setLoading(false);
    }
  }, [filter, settings]);

  useEffect(() => {
    const loadSettings = async () => {
      try {
        const res = await api.get('/volume-scanner/settings');
        if (res.data.settings) {
          setSettings(res.data.settings);
        }
      } catch (err) {
        console.error('Failed to load settings:', err);
      }
    };
    loadSettings();
  }, []);

  useEffect(() => {
    fetchData();
    const interval = setInterval(fetchData, 30000); // 30 seconds instead of 15
    return () => clearInterval(interval);
  }, [fetchData]);

  const saveSettings = async () => {
    setSaving(true);
    try {
      await api.post('/volume-scanner/settings', settings);
      setShowSettings(false);
    } catch (err) {
      alert('Failed to save settings');
    } finally {
      setSaving(false);
    }
  };

  const triggerEntry = async (alertId) => {
    if (!window.confirm('Trigger entry from this alert?')) return;
    try {
      const res = await api.post(`/volume-scanner/trigger/${alertId}`);
      alert(`Entry created: ${res.data.position?.tokenSymbol}`);
      fetchData();
    } catch (err) {
      alert(err.response?.data?.error || 'Failed to trigger entry');
    }
  };

  const dismissAlert = async (alertId) => {
    try {
      await api.delete(`/volume-scanner/alerts/${alertId}`);
      fetchData();
    } catch (err) {
      alert('Failed to dismiss alert');
    }
  };

  const getScoreColor = (score) => {
    if (score >= 80) return 'bg-green-500';
    if (score >= 60) return 'bg-yellow-500';
    if (score >= 40) return 'bg-orange-500';
    return 'bg-red-500';
  };

  const getAlertTypeLabel = (type) => {
    const labels = {
      volume_spike: 'Volume',
      buy_pressure: 'Buys',
      velocity_surge: 'Velocity',
      whale_buy: 'Whale',
      combined: 'Combined'
    };
    return labels[type] || type;
  };

  const formatNumber = (n, decimals = 2) => {
    if (!n) return '0';
    if (n >= 1e6) return (n / 1e6).toFixed(decimals) + 'M';
    if (n >= 1e3) return (n / 1e3).toFixed(decimals) + 'K';
    return n.toFixed(decimals);
  };

  const getTradeUrl = (chain, address) => {
    if (chain === 'solana' || chain === 'bsc') {
      return `https://axiom.trade/t/${address}`;
    }
    if (chain === 'base') {
      return `https://gmgn.ai/base/token/${address}`;
    }
    if (chain === 'ethereum') {
      return `https://gmgn.ai/eth/token/${address}`;
    }
    return null;
  };

  const getTradeLabel = (chain) => {
    if (chain === 'solana' || chain === 'bsc') return 'Axiom';
    if (chain === 'base' || chain === 'ethereum') return 'GMGN';
    return 'Trade';
  };

  const getFomoUrl = (chain, address) => {
    const slug = { solana: 'solana', ethereum: 'ethereum', base: 'base', bsc: 'bnb' }[chain];
    return slug && address ? `https://fomo.family/tokens/${slug}/${address}` : null;
  };

  const getChainEmoji = (chain) => {
    const emojis = { solana: '◎', base: '🔵', ethereum: '⟠', bsc: '🟡' };
    return emojis[chain] || '🔗';
  };

  const canManualEnter = (alert) => !alert.triggeredEntry && (alert.status === 'active' || alert.status === 'expired');

  const getStatusHint = (alert) => {
    if (alert.status === 'triggered' || alert.triggeredEntry) return 'Entry taken';
    if (alert.status === 'expired') {
      const threshold = settings?.autoEntryScoreThreshold ?? 70;
      if (alert.alertScore < threshold) {
        return `Expired · score ${alert.alertScore} < auto ${threshold}`;
      }
      return 'Expired · window closed';
    }
    if (alert.status === 'active' && alert.alertScore < (settings?.autoEntryScoreThreshold ?? 70)) {
      return `Manual only · score ${alert.alertScore}`;
    }
    return null;
  };

  if (loading) {
    return (
      <div className="p-6">
        <h1 className="text-2xl font-bold text-gray-900 dark:text-white mb-4">Volume Monitor</h1>
        <div className="flex items-center justify-center h-64">
          <div className="text-gray-500">Loading...</div>
        </div>
      </div>
    );
  }

  return (
    <div className="p-6 max-w-7xl mx-auto">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <h1 className="text-2xl font-bold text-gray-900 dark:text-white">Volume Monitor</h1>
        <button
          onClick={() => setShowSettings(!showSettings)}
          className={`px-4 py-2 rounded-lg text-sm font-medium ${
            showSettings ? 'bg-indigo-600 text-white' : 'bg-gray-200 dark:bg-gray-700 text-gray-700 dark:text-gray-300'
          }`}
        >
          {showSettings ? 'Hide Settings' : 'Settings'}
        </button>
      </div>

      {/* Stats Cards */}
      {stats && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
          <div className="bg-white dark:bg-gray-800 rounded-lg p-4 border-t-4 border-blue-500">
            <div className="text-sm text-gray-500 dark:text-gray-400">Alerts (24h)</div>
            <div className="text-2xl font-bold text-blue-600">{stats.totalAlerts24h}</div>
          </div>
          <div className="bg-white dark:bg-gray-800 rounded-lg p-4 border-t-4 border-green-500">
            <div className="text-sm text-gray-500 dark:text-gray-400">Active Alerts</div>
            <div className="text-2xl font-bold text-green-600">{stats.activeAlerts}</div>
          </div>
          <div className="bg-white dark:bg-gray-800 rounded-lg p-4 border-t-4 border-purple-500">
            <div className="text-sm text-gray-500 dark:text-gray-400">Entries Triggered</div>
            <div className="text-2xl font-bold text-purple-600">{stats.entriesTriggered24h}</div>
          </div>
          <div className="bg-white dark:bg-gray-800 rounded-lg p-4 border-t-4 border-amber-500">
            <div className="text-sm text-gray-500 dark:text-gray-400">Avg Score</div>
            <div className="text-2xl font-bold text-amber-600">{Math.round(stats.avgScore24h) || 0}</div>
          </div>
        </div>
      )}

      {/* Settings Panel */}
      {showSettings && settings && (
        <div className="bg-white dark:bg-gray-800 rounded-lg p-6 mb-6 shadow">
          <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">Scanner Settings</h3>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <div className="flex items-center gap-3">
              <label className="text-sm text-gray-600 dark:text-gray-400">Enabled</label>
              <button
                onClick={() => setSettings({ ...settings, enabled: !settings.enabled })}
                className={`w-12 h-6 rounded-full relative transition-colors ${settings.enabled ? 'bg-green-500' : 'bg-gray-400'}`}
              >
                <span className={`absolute top-1 w-4 h-4 bg-white rounded-full transition-all ${settings.enabled ? 'left-7' : 'left-1'}`} />
              </button>
            </div>
            <div>
              <label className="block text-xs text-gray-500 dark:text-gray-400 mb-1">Volume Multiple</label>
              <input
                type="number"
                value={settings.volumeMultipleThreshold}
                onChange={(e) => setSettings({ ...settings, volumeMultipleThreshold: parseFloat(e.target.value) || 3 })}
                className="w-full px-3 py-2 bg-gray-100 dark:bg-gray-700 rounded text-sm"
              />
            </div>
            <div>
              <label className="block text-xs text-gray-500 dark:text-gray-400 mb-1">Buy Ratio %</label>
              <input
                type="number"
                value={settings.buyRatioThreshold}
                onChange={(e) => setSettings({ ...settings, buyRatioThreshold: parseFloat(e.target.value) || 65 })}
                className="w-full px-3 py-2 bg-gray-100 dark:bg-gray-700 rounded text-sm"
              />
            </div>
            <div>
              <label className="block text-xs text-gray-500 dark:text-gray-400 mb-1">Auto-Entry Score</label>
              <input
                type="number"
                value={settings.autoEntryScoreThreshold}
                onChange={(e) => setSettings({ ...settings, autoEntryScoreThreshold: parseFloat(e.target.value) || 70 })}
                className="w-full px-3 py-2 bg-gray-100 dark:bg-gray-700 rounded text-sm"
              />
            </div>
            <div>
              <label className="block text-xs text-gray-500 dark:text-gray-400 mb-1">Alert window (min)</label>
              <input
                type="number"
                min="15"
                step="15"
                value={settings.alertTtlMinutes ?? 90}
                onChange={(e) => setSettings({ ...settings, alertTtlMinutes: parseInt(e.target.value, 10) || 90 })}
                className="w-full px-3 py-2 bg-gray-100 dark:bg-gray-700 rounded text-sm"
              />
            </div>
            <div>
              <label className="block text-xs text-gray-500 dark:text-gray-400 mb-1">Min Volume 5m ($)</label>
              <input
                type="number"
                value={settings.minVolume5m}
                onChange={(e) => setSettings({ ...settings, minVolume5m: parseFloat(e.target.value) || 500 })}
                className="w-full px-3 py-2 bg-gray-100 dark:bg-gray-700 rounded text-sm"
              />
            </div>
            <div>
              <label className="block text-xs text-gray-500 dark:text-gray-400 mb-1">Max Market Cap ($)</label>
              <input
                type="number"
                value={settings.maxMarketCap}
                onChange={(e) => setSettings({ ...settings, maxMarketCap: parseFloat(e.target.value) || 2000000 })}
                className="w-full px-3 py-2 bg-gray-100 dark:bg-gray-700 rounded text-sm"
              />
            </div>
            <div>
              <label className="block text-xs text-gray-500 dark:text-gray-400 mb-1">Min Recovery %</label>
              <input
                type="number"
                value={settings.minRecoveryPercent}
                onChange={(e) => setSettings({ ...settings, minRecoveryPercent: parseFloat(e.target.value) || 5 })}
                className="w-full px-3 py-2 bg-gray-100 dark:bg-gray-700 rounded text-sm"
              />
            </div>
            <div>
              <label className="block text-xs text-gray-500 dark:text-gray-400 mb-1">Max Recovery %</label>
              <input
                type="number"
                value={settings.maxRecoveryPercent}
                onChange={(e) => setSettings({ ...settings, maxRecoveryPercent: parseFloat(e.target.value) || 500 })}
                className="w-full px-3 py-2 bg-gray-100 dark:bg-gray-700 rounded text-sm"
              />
            </div>
          </div>
          <div className="mt-4">
            <button
              onClick={saveSettings}
              disabled={saving}
              className="px-4 py-2 bg-green-600 text-white rounded-lg text-sm font-medium hover:bg-green-700 disabled:opacity-50"
            >
              {saving ? 'Saving...' : 'Save Settings'}
            </button>
          </div>
        </div>
      )}

      {/* Filters */}
      <div className="flex gap-3 mb-4 flex-wrap">
        <select
          value={filter.status}
          onChange={(e) => setFilter({ ...filter, status: e.target.value })}
          className="px-3 py-2 bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-lg text-sm"
        >
          <option value="">All Status</option>
          <option value="active">Active</option>
          <option value="triggered">Triggered</option>
          <option value="expired">Expired</option>
        </select>
        <select
          value={filter.minScore}
          onChange={(e) => setFilter({ ...filter, minScore: e.target.value })}
          className="px-3 py-2 bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-lg text-sm"
        >
          <option value="">All Scores</option>
          <option value="80">Score 80+</option>
          <option value="60">Score 60+</option>
          <option value="40">Score 40+</option>
        </select>
        <button
          onClick={fetchData}
          className="px-4 py-2 bg-blue-600 text-white rounded-lg text-sm font-medium hover:bg-blue-700"
        >
          Refresh
        </button>
      </div>

      {/* Alerts Table */}
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow overflow-hidden">
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
            <thead className="bg-gray-50 dark:bg-gray-900">
              <tr>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">Token</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">Score</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">Type</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">Vol 5m</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">Vol x</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">Buys</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">Recovery</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">MC</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">Status</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
              {alerts.length === 0 ? (
                <tr>
                  <td colSpan={10} className="px-4 py-12 text-center text-gray-500 dark:text-gray-400">
                    No alerts found. The scanner monitors recovery tokens for volume spikes.
                  </td>
                </tr>
              ) : (
                alerts.map((alert) => (
                  <tr key={alert._id} className="hover:bg-gray-50 dark:hover:bg-gray-700/50">
                    <td className="px-4 py-3">
                      <button
                        onClick={() => navigate(`/token/${alert.chain}/${alert.tokenAddress}`)}
                        className="text-left hover:bg-gray-100 dark:hover:bg-gray-700 rounded p-1 -m-1"
                      >
                        <div className="flex items-center gap-2">
                          <span className="text-lg">{getChainEmoji(alert.chain)}</span>
                          <div>
                            <div className="text-sm font-medium text-blue-600 hover:text-blue-800">
                              {alert.tokenSymbol}
                              {alert.alertCount > 1 && (
                                <span className="ml-1 text-xs bg-blue-100 dark:bg-blue-900 text-blue-600 dark:text-blue-300 px-1 rounded">
                                  x{alert.alertCount}
                                </span>
                              )}
                            </div>
                            <div className="text-xs text-gray-400 font-mono">{alert.tokenAddress?.slice(0, 6)}...{alert.tokenAddress?.slice(-4)}</div>
                          </div>
                        </div>
                      </button>
                    </td>
                    <td className="px-4 py-3">
                      <span className={`inline-block px-2 py-1 rounded-full text-xs font-bold text-white ${getScoreColor(alert.alertScore)}`}>
                        {alert.alertScore}
                      </span>
                    </td>
                    <td className="px-4 py-3">
                      <span className="text-xs text-gray-500 dark:text-gray-400 bg-gray-100 dark:bg-gray-700 px-2 py-1 rounded">
                        {getAlertTypeLabel(alert.alertType)}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-sm text-gray-700 dark:text-gray-300">${formatNumber(alert.volume5m)}</td>
                    <td className="px-4 py-3">
                      <span className={`text-sm font-medium ${alert.volumeMultiple >= 3 ? 'text-green-600' : 'text-gray-500'}`}>
                        {alert.volumeMultiple?.toFixed(1)}x
                      </span>
                    </td>
                    <td className="px-4 py-3">
                      <span className={`text-sm font-medium ${alert.buyRatio >= 65 ? 'text-green-600' : 'text-gray-500'}`}>
                        {alert.buyRatio?.toFixed(0)}%
                      </span>
                    </td>
                    <td className="px-4 py-3 text-sm text-gray-700 dark:text-gray-300">{alert.recoveryPercent?.toFixed(0)}%</td>
                    <td className="px-4 py-3 text-sm text-gray-700 dark:text-gray-300">${formatNumber(alert.marketCapAtAlert)}</td>
                    <td className="px-4 py-3">
                      <span className={`px-2 py-1 rounded text-xs font-medium ${
                        alert.status === 'active' ? 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-400' :
                        alert.status === 'triggered' ? 'bg-purple-100 text-purple-800 dark:bg-purple-900/30 dark:text-purple-400' :
                        'bg-gray-100 text-gray-600 dark:bg-gray-700 dark:text-gray-400'
                      }`}>
                        {alert.status}
                      </span>
                      {getStatusHint(alert) && (
                        <div className="text-[10px] text-gray-400 mt-1 max-w-[120px]">{getStatusHint(alert)}</div>
                      )}
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-2">
                        <button
                          onClick={() => setChartModal({ isOpen: true, alert })}
                          className="px-2 py-1 bg-green-500 text-white rounded text-xs hover:bg-green-600"
                          title="View Chart"
                        >
                          Chart
                        </button>
                        {getFomoUrl(alert.chain, alert.tokenAddress) && (
                          <a
                            href={getFomoUrl(alert.chain, alert.tokenAddress)}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="px-2 py-1 bg-orange-500 text-white rounded text-xs hover:bg-orange-600"
                          >
                            Fomo
                          </a>
                        )}
                        {getTradeUrl(alert.chain, alert.tokenAddress) && (
                          <a
                            href={getTradeUrl(alert.chain, alert.tokenAddress)}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="px-2 py-1 bg-purple-500 text-white rounded text-xs hover:bg-purple-600"
                          >
                            {getTradeLabel(alert.chain)}
                          </a>
                        )}
                        {canManualEnter(alert) && (
                          <>
                            <button
                              onClick={() => triggerEntry(alert._id)}
                              className="px-2 py-1 bg-blue-500 text-white rounded text-xs hover:bg-blue-600"
                            >
                              Enter
                            </button>
                            {alert.status === 'active' && (
                              <button
                                onClick={() => dismissAlert(alert._id)}
                                className="px-2 py-1 bg-gray-500 text-white rounded text-xs hover:bg-gray-600"
                              >
                                Dismiss
                              </button>
                            )}
                          </>
                        )}
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Chart Modal */}
      {chartModal.isOpen && chartModal.alert && (
        <ChartModal
          alert={chartModal.alert}
          onClose={() => setChartModal({ isOpen: false, alert: null })}
          onNavigate={() => navigate(`/token/${chartModal.alert.chain}/${chartModal.alert.tokenAddress}`)}
          getTradeUrl={getTradeUrl}
          getTradeLabel={getTradeLabel}
          getFomoUrl={getFomoUrl}
          formatNumber={formatNumber}
        />
      )}
    </div>
  );
}

function ChartModal({ alert, onClose, onNavigate, getTradeUrl, getTradeLabel, getFomoUrl, formatNumber }) {
  const embedUrl = `https://dexscreener.com/${alert.chain}/${alert.tokenAddress}?embed=1&theme=dark&trades=0&info=0`;

  return (
    <div className="fixed inset-0 z-50 overflow-hidden" onClick={onClose}>
      <div className="absolute inset-0 bg-black bg-opacity-75" />
      <div className="flex items-center justify-center min-h-screen p-4">
        <div
          className="relative bg-gray-900 rounded-lg shadow-2xl w-full max-w-6xl h-[85vh] flex flex-col"
          onClick={(e) => e.stopPropagation()}
        >
          {/* Header */}
          <div className="flex items-center justify-between px-4 py-3 border-b border-gray-700">
            <div className="flex items-center gap-4">
              <button
                onClick={onNavigate}
                className="text-white font-semibold text-lg hover:text-blue-400 transition-colors"
              >
                {alert.tokenSymbol}
              </button>
              <span className={`px-3 py-1 rounded-full text-sm font-bold text-white ${
                alert.alertScore >= 80 ? 'bg-green-500' :
                alert.alertScore >= 60 ? 'bg-yellow-500' :
                alert.alertScore >= 40 ? 'bg-orange-500' : 'bg-red-500'
              }`}>
                Score: {alert.alertScore}
              </span>
            </div>
            <button onClick={onClose} className="text-gray-400 hover:text-white p-1">
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>

          {/* Info Bar */}
          <div className="flex items-center gap-6 px-4 py-2 bg-gray-800 text-sm">
            <div>
              <span className="text-gray-400">Volume 5m:</span>
              <span className="ml-2 text-white font-medium">${formatNumber(alert.volume5m)}</span>
            </div>
            <div>
              <span className="text-gray-400">Vol Multiple:</span>
              <span className={`ml-2 font-medium ${alert.volumeMultiple >= 3 ? 'text-green-400' : 'text-gray-300'}`}>
                {alert.volumeMultiple?.toFixed(1)}x
              </span>
            </div>
            <div>
              <span className="text-gray-400">Buy Ratio:</span>
              <span className={`ml-2 font-medium ${alert.buyRatio >= 65 ? 'text-green-400' : 'text-gray-300'}`}>
                {alert.buyRatio?.toFixed(0)}%
              </span>
            </div>
            <div>
              <span className="text-gray-400">Recovery:</span>
              <span className="ml-2 text-white font-medium">{alert.recoveryPercent?.toFixed(0)}%</span>
            </div>
            <div>
              <span className="text-gray-400">MC:</span>
              <span className="ml-2 text-white font-medium">${formatNumber(alert.marketCapAtAlert)}</span>
            </div>
          </div>

          {/* Chart */}
          <div className="flex-1 relative">
            <iframe
              src={embedUrl}
              title={`Chart - ${alert.tokenSymbol}`}
              className="absolute inset-0 w-full h-full border-0"
              allow="clipboard-write"
              loading="lazy"
            />
          </div>

          {/* Footer */}
          <div className="flex items-center justify-between px-4 py-3 border-t border-gray-700 bg-gray-800">
            <div className="text-xs text-gray-400 font-mono">{alert.tokenAddress}</div>
            <div className="flex gap-2">
              <button
                onClick={() => navigator.clipboard.writeText(alert.tokenAddress)}
                className="px-3 py-1 bg-gray-700 text-gray-300 rounded text-xs hover:bg-gray-600"
              >
                Copy CA
              </button>
              <a
                href={`https://dexscreener.com/${alert.chain}/${alert.tokenAddress}`}
                target="_blank"
                rel="noopener noreferrer"
                className="px-3 py-1 bg-green-600 text-white rounded text-xs hover:bg-green-700"
              >
                DexScreener
              </a>
              {getFomoUrl(alert.chain, alert.tokenAddress) && (
                <a
                  href={getFomoUrl(alert.chain, alert.tokenAddress)}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="px-3 py-1 bg-orange-600 text-white rounded text-xs hover:bg-orange-700"
                >
                  Fomo
                </a>
              )}
              {getTradeUrl(alert.chain, alert.tokenAddress) && (
                <a
                  href={getTradeUrl(alert.chain, alert.tokenAddress)}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="px-3 py-1 bg-purple-600 text-white rounded text-xs hover:bg-purple-700"
                >
                  {getTradeLabel(alert.chain)}
                </a>
              )}
              <button
                onClick={onNavigate}
                className="px-3 py-1 bg-blue-600 text-white rounded text-xs hover:bg-blue-700"
              >
                Token Details
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
